/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import PropTypes from "prop-types";
import { useTheme } from "@mui/material";

// Components
import Icon from "@mui/material/Icon";

// Components
import Box from "components/Atoms/Box";

// Styles
import borders from "assets/theme/base/borders";
import typography from "assets/theme/base/typography";

function DataTableHeadCell({ width, children, sorted, align, toSort, colSpan, ...rest }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;

  // Theme-aware border color
  const borderColor = isDark ? "rgba(255, 255, 255, 0.12)" : theme.palette.grey[300];

  return (
    <Box
      component="th"
      width={width}
      borderBottom={`${borderWidth[1]} solid ${borderColor}`}
      py={1.5}
      px={3}
      colSpan={colSpan}
    >
      <Box
        {...rest}
        position="relative"
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={isDark ? 0.9 : 0.7}
        customClass={`text-uppercase ${sorted && "cursor-pointer user-select-none"}`}
      >
        {children}
        {sorted && (
          <Box
            fontSize={size.lg}
            position="absolute"
            top={0}
            right={align !== "right" ? "16px" : 0}
            left={align === "right" ? "-5px" : "unset"}
          >
            <Box
              position="absolute"
              top={-6}
              color={sorted === "asce" ? "text" : "secondary"}
              opacity={sorted === "asce" ? 1 : 0.5}
            >
              {toSort && <Icon className="">arrow_drop_up</Icon>}
            </Box>
            <Box
              position="absolute"
              top={0}
              color={sorted === "desc" ? "text" : "secondary"}
              opacity={sorted === "desc" ? 1 : 0.5}
            >
              {toSort && <Icon className="">arrow_drop_down</Icon>}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Setting default values for the props of DataTableHeadCell
DataTableHeadCell.defaultProps = {
  width: "auto",
  sorted: "none",
  align: "left",
  toSort: true,
  colSpan: 1,
};

// Typechecking props for the DataTableHeadCell
DataTableHeadCell.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  sorted: PropTypes.oneOf([false, "none", "asce", "desc"]),
  align: PropTypes.oneOf(["left", "right", "center"]),
  toSort: PropTypes.bool,
  colSpan: PropTypes.number,
};

export default DataTableHeadCell;
